// Dates clés de la saison — AUCUNE date n'est écrite dans ce fichier.
//
// 1) La rentrée est DÉDUITE du calendrier officiel des vacances vaudoises
//    (`vacancesScolaires.js`) selon la règle de l'école : reprise une semaine après la
//    rentrée scolaire officielle. Elle avance donc d'elle-même chaque année.
// 2) Les PORTES OUVERTES sont saisies par l'école dans le classeur (onglet `Listes`,
//    colonnes AA « du » et AB « au ») et lues par `useReglages()`. Les fonctions ci-dessous
//    ne font que les mettre en forme. Si le classeur est injoignable, le site n'affiche
//    aucune date plutôt qu'une date périmée.
import { RENTREES } from "./vacancesScolaires";

const JOUR_MOIS = { day: "numeric", month: "long" };
const JOUR_MOIS_AN = { day: "numeric", month: "long", year: "numeric" };

function isoJour(d) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const j = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${j}`;
}

// La saison de l'école commence en juillet : de juillet à décembre on regarde la rentrée de
// l'année en cours, de janvier à juin celle de l'été précédent (le semestre 2 appartient à la
// même saison). Aucune date à mettre à jour.
function rentreeDeLaSaison(now = new Date()) {
  const anneeSaison = now.getMonth() + 1 >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  const debut = `${anneeSaison}-07-01`;
  return RENTREES.find((iso) => iso >= debut) || RENTREES[RENTREES.length - 1];
}

function saisonDepuis(rentreeIso) {
  const date = new Date(`${rentreeIso}T00:00:00`);
  const annee = Number(rentreeIso.slice(0, 4));
  return {
    // « 2026-2027 »
    libelle: `${annee}-${annee + 1}`,
    // Reprise des cours après l'été
    rentree: {
      iso: rentreeIso,
      label: date.toLocaleDateString("fr-CH", JOUR_MOIS),
      label_long: `${date.toLocaleDateString("fr-CH", { weekday: "long" })} ${date.toLocaleDateString("fr-CH", JOUR_MOIS_AN)}`,
    },
    // Date à laquelle l'âge est calculé pour choisir le barème (enfants/ados vs adultes).
    // Figée au début de la saison : le même élève paie le même prix qu'il s'inscrive en août
    // ou en janvier.
    tarif_age_reference: {
      iso: rentreeIso,
      label: date.toLocaleDateString("fr-CH", JOUR_MOIS_AN),
    },
  };
}

export const SAISON = saisonDepuis(rentreeDeLaSaison());

// Appelé quand le calendrier des Réglages de la console est arrivé (useReglages) : la
// rentrée saisie par l'école remplace celle déduite du calendrier officiel, sur place.
export function rafraichirSaison() {
  Object.assign(SAISON, saisonDepuis(rentreeDeLaSaison()));
}

// ------------------------------------------------------------------ portes ouvertes
// `fenetres` = [{ debut: "2026-08-24", fin: "2026-09-05" }, …] tel que renvoyé par
// `GET /api/reglages` (donc par le classeur de l'école).

export function fenetreEnCours(fenetres, now = new Date()) {
  const j = isoJour(now);
  return (fenetres || []).find((f) => f.debut <= j && j <= f.fin) || null;
}

// La fenêtre en cours, sinon la prochaine à venir. Null quand la saison n'en a plus.
export function prochaineFenetre(fenetres, now = new Date()) {
  const j = isoJour(now);
  return (fenetres || []).find((f) => f.fin >= j) || null;
}

export function estEnPortesOuvertes(date, fenetres) {
  const j = typeof date === "string" ? date.slice(0, 10) : isoJour(date);
  return (fenetres || []).some((f) => f.debut <= j && j <= f.fin);
}

// « du 24 août au 5 septembre 2026 » — et « du 11 au 16 janvier 2027 » dans un même mois.
export function libelleFenetre(f) {
  if (!f) return "";
  const debut = new Date(`${f.debut}T00:00:00`);
  const fin = new Date(`${f.fin}T00:00:00`);
  const memeMois = f.debut.slice(0, 7) === f.fin.slice(0, 7);
  const debutTxt = memeMois ? String(debut.getDate()) : debut.toLocaleDateString("fr-CH", JOUR_MOIS);
  return `du ${debutTxt} au ${fin.toLocaleDateString("fr-CH", JOUR_MOIS_AN)}`;
}

// « 24 août - 5 sept. 2026 » — format court pour les métadonnées.
export function libelleFenetreCourt(f) {
  if (!f) return "";
  const debut = new Date(`${f.debut}T00:00:00`);
  const fin = new Date(`${f.fin}T00:00:00`);
  const court = { day: "numeric", month: "short" };
  return `${debut.toLocaleDateString("fr-CH", court)} - ${fin.toLocaleDateString("fr-CH", { ...court, year: "numeric" })}`;
}

// Bandeau d'annonce et pastille de l'accueil : visibles pendant une fenêtre de portes
// ouvertes et les trois semaines qui la précèdent. Tout s'allume et s'éteint sans
// intervention, au rythme des dates saisies dans le classeur.
export function annonceActive(fenetres, now = new Date(), joursAvant = 21) {
  const f = prochaineFenetre(fenetres, now);
  if (!f) return false;
  const limite = new Date(now);
  limite.setDate(limite.getDate() + joursAvant);
  return f.debut <= isoJour(limite);
}

// Vrai une fois la rentrée passée : le texte de l'annonce change alors tout seul
// (« Reprise des cours dès le … » → « Les cours ont repris … »).
export function rentreePassee(now = new Date()) {
  return isoJour(now) > SAISON.rentree.iso;
}

export function titreAnnonce(now = new Date()) {
  return rentreePassee(now)
    ? "Les cours ont repris — il reste de la place"
    : `Reprise des cours dès le ${SAISON.rentree.label}`;
}

// Cours qui ne prennent plus de nouvel élève d'ici juin (colonne Z du planning = « Non »).
// Le cours reste TOUJOURS réservable : on prévient la famille, on ne l'empêche de rien.
// Fenêtre du 1er février au 30 juin ; en juillet et en août l'inscription vaut naturellement
// pour la rentrée, le message n'aurait plus de sens. Se réactive seule chaque année.
export const MESSAGE_INSCRIPTION_TARDIVE =
  "Ce cours prépare le spectacle de fin d'année et ne prend plus de nouvel élève d'ici juin. " +
  "Votre inscription est enregistrée en liste d'attente pour la rentrée de fin août.";

// Libellé court, pour les listes et les badges du planning.
export const LABEL_PLACES_RENTREE = "Places pour la rentrée";

// Bascule spectacle (« JJ-MM », 1er février par défaut) : réglée par l'école dans la
// console (Réglages → Saison → date_limite_spectacle). Appliquée dès que `/api/reglages`
// la fournit ; sinon le repli du 1er février reste en place.
const BASCULE = { jour: 1, mois: 2 };

const MOIS_LONGS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août",
  "septembre", "octobre", "novembre", "décembre"];

export function appliquerBasculeSpectacle(valeur) {
  const m = String(valeur || "").trim().match(/^(\d{1,2})-(\d{1,2})$/);
  if (!m) return;
  const jour = Number(m[1]);
  const mois = Number(m[2]);
  if (mois < 1 || mois > 12 || jour < 1 || jour > 31) return;
  BASCULE.jour = jour;
  BASCULE.mois = mois;
}

/** « 1er février », « 15 janvier » : le premier jour sans essai ni nouvel élève. */
export function libelleBasculeSpectacle() {
  return `${BASCULE.jour === 1 ? "1er" : BASCULE.jour} ${MOIS_LONGS[BASCULE.mois - 1]}`;
}

/** « 31 janvier » : le dernier jour où l'essai est encore possible. */
export function libelleVeilleBasculeSpectacle(now = new Date()) {
  const annee = now.getMonth() + 1 >= 8 ? now.getFullYear() + 1 : now.getFullYear();
  const d = new Date(annee, BASCULE.mois - 1, BASCULE.jour);
  d.setDate(d.getDate() - 1);
  return `${d.getDate() === 1 ? "1er" : d.getDate()} ${MOIS_LONGS[d.getMonth()]}`;
}

function apresBascule(now) {
  const mois = now.getMonth() + 1;
  const jour = now.getDate();
  return mois > BASCULE.mois || (mois === BASCULE.mois && jour >= BASCULE.jour);
}

export function inscriptionPourLaRentree(course, now = new Date()) {
  const mois = now.getMonth() + 1;
  if (!apresBascule(now) || mois > 6) return false;
  return String(course?.late_join || "").trim().toLowerCase() === "non";
}

export function messageInscriptionTardive(course, now = new Date()) {
  return inscriptionPourLaRentree(course, now) ? MESSAGE_INSCRIPTION_TARDIVE : "";
}

// Cours d'essai suspendus pour les cours qui participent au spectacle de fin d'année
// (colonne AB du planning = « Oui ») : les chorégraphies sont en place et les répétitions ne
// peuvent plus être interrompues. La fenêtre va du 1er février jusqu'à la VEILLE des
// prochaines Portes Ouvertes (celles de juin, saisies dans le classeur) : c'est là que l'essai
// redevient possible. Aucune date écrite en dur, tout se réactive seul chaque année.
export function participeAuSpectacle(course) {
  return String(course?.participation_spectacle || "").trim().toLowerCase() === "oui";
}

function veille(iso) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() - 1);
  return isoJour(d);
}

export function fenetreEssaisSuspendus(fenetresPO = [], now = new Date()) {
  const mois = now.getMonth() + 1;
  const annee = mois >= 8 ? now.getFullYear() + 1 : now.getFullYear();
  const debut_iso = `${annee}-${String(BASCULE.mois).padStart(2, "0")}-${String(BASCULE.jour).padStart(2, "0")}`;
  const reprise = [...(fenetresPO || [])]
    .filter((f) => f.debut > debut_iso)
    .sort((a, b) => a.debut.localeCompare(b.debut))[0] || null;
  const fin_iso = reprise ? veille(reprise.debut) : `${annee}-07-31`;
  const end = new Date(`${fin_iso}T00:00:00`);
  end.setHours(23, 59, 59, 999);
  return {
    start: new Date(`${debut_iso}T00:00:00`),
    end,
    debut_iso,
    fin_iso,
    reprise,
    label: "Préparation du spectacle — pas de cours d'essai",
    poAutorisee: true,
  };
}

/** Vrai uniquement pendant la fenêtre de suspension (donc dès la bascule spectacle). */
export function suspensionEssaisActive(fenetresPO = [], now = new Date()) {
  const f = fenetreEssaisSuspendus(fenetresPO, now);
  const j = isoJour(now);
  return j >= f.debut_iso && j <= f.fin_iso;
}

/** Vrai si cette date d'essai est refusée pour ce cours. */
export function essaiSuspendu(course, dateIso, fenetresPO, now = new Date()) {
  if (!participeAuSpectacle(course) || !dateIso) return false;
  if (estEnPortesOuvertes(dateIso, fenetresPO)) return false;
  const f = fenetreEssaisSuspendus(fenetresPO, now);
  return dateIso >= f.debut_iso && dateIso <= f.fin_iso;
}
